import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { type Rule, useComposerRules } from "@/lib/api";

import { TierBadge } from "./tier-badge";
import type { GuardrailCategory } from "./types";
import { apiErrorMessage, titleCase } from "./utils";

const CATEGORY_BLURB: Record<GuardrailCategory, string> = {
  brand: "What the output is allowed to be built on, and what it may inherit from the template.",
  imagery: "Which pictures may be placed, and where they may come from.",
  text_in_visuals: "What may be changed in text that lives inside a designed image or layout.",
  translation: "How copy is translated, and when a translation is flagged rather than trusted.",
  length: "How much may be cut or kept, and who decides.",
  escalation: "What the composer refuses outright and hands to the Strategic Comms team.",
};

/**
 * The authored rule book, one category at a time.
 *
 * These are the durable artifact — `data/rules/guardrails.yaml` outlives the
 * prototype — so they get the main container rather than a slide-over nobody
 * opens.
 */
export function RulesView({ category }: { category: GuardrailCategory }) {
  const rulesQuery = useComposerRules();
  const rules = (rulesQuery.data ?? []).filter((rule) => rule.category === category);

  if (rulesQuery.isLoading) {
    return (
      <p className="text-muted flex items-center gap-2 py-8 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading rules…
      </p>
    );
  }

  if (rulesQuery.isError) {
    return (
      <p className="text-outcome-blocked py-8 text-sm">
        {apiErrorMessage(rulesQuery.error, "Could not load the rule set.")}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="type-label text-muted">Guardrails</p>
        <h2 className="font-heading text-foreground text-2xl font-bold">{titleCase(category)}</h2>
        <p className="text-muted max-w-2xl text-sm leading-6">{CATEGORY_BLURB[category]}</p>
      </header>

      {rules.length === 0 ? (
        <p className="text-muted text-sm">No rules in this category yet.</p>
      ) : (
        <ul className="space-y-3">
          {rules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RuleCard({ rule }: { rule: Rule }) {
  return (
    <li className="border-border bg-card rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-foreground flex-1 text-sm font-bold">{rule.title}</h3>
        {rule.tier ? (
          <TierBadge tier={rule.tier} />
        ) : (
          // A rule with no tier does not resolve into a change — it blocks, and
          // blocking is the whole outcome.
          <Badge variant="outline" className="border-outcome-blocked/40 text-outcome-blocked">
            Absolute
          </Badge>
        )}
      </div>
      <p className="text-muted mt-2 text-sm leading-6">{rule.description}</p>
      <code className="text-faint mt-3 block text-[11px]">{rule.id}</code>
    </li>
  );
}
